const ELEMENT_TYPES = new Set(['vertex', 'edge', 'face']);

/**
 * Sub-object selection for one editable mesh at a time — deliberately a
 * separate state from `selection-state.js`'s object-level selection, not a
 * mode layered on top of it. Entering mesh Edit Mode does not touch, clear,
 * or otherwise contaminate the object selection; the object stays selected
 * exactly as it was, and this tracks which of *its* vertices/edges/faces are
 * selected, addressed by their stable topology id (never a raw index — see
 * mesh-topology.js).
 *
 * UI-only, like `activeTool` in main.js: entering/exiting edit mode and
 * switching vertex/edge/face mode are not bridge commands and do not enter
 * undo history, mirroring how switching the Move/Rotate/Scale tool doesn't
 * either.
 */
export function createMeshSelectionState({ onChange = () => {} } = {}) {
  let meshId = null;
  let elementType = 'vertex';
  let selected = new Set();

  function snapshot() {
    return { meshId, elementType, ids: [...selected], active: meshId != null };
  }

  function emit() {
    onChange(snapshot());
  }

  return {
    get meshId() {
      return meshId;
    },
    get elementType() {
      return elementType;
    },
    get active() {
      return meshId != null;
    },
    get ids() {
      return [...selected];
    },
    get activeId() {
      return selected.size > 0 ? [...selected].at(-1) : null;
    },

    has(id) {
      return selected.has(id);
    },

    /** Enter edit mode for one mesh object, starting with no elements selected. */
    enter(id, type = 'vertex') {
      if (!id) throw new Error('enter() requires a mesh object id');
      if (!ELEMENT_TYPES.has(type)) throw new Error(`unknown mesh element type '${type}'`);
      meshId = id;
      elementType = type;
      selected = new Set();
      emit();
    },

    /** Leave edit mode entirely. A no-op if already inactive, so callers can call it defensively. */
    exit() {
      if (meshId == null) return false;
      meshId = null;
      elementType = 'vertex';
      selected = new Set();
      emit();
      return true;
    },

    /** Switching element mode always clears the current selection — a vertex id means nothing in face mode. */
    setElementType(type) {
      if (!ELEMENT_TYPES.has(type)) throw new Error(`unknown mesh element type '${type}'`);
      if (meshId == null || type === elementType) return;
      elementType = type;
      selected = new Set();
      emit();
    },

    select(id, { toggle = false } = {}) {
      if (meshId == null || !id) return;
      if (!toggle) {
        if (selected.size === 1 && selected.has(id)) return;
        selected = new Set([id]);
      } else if (selected.has(id)) {
        selected.delete(id);
        selected = new Set(selected);
      } else {
        selected = new Set([...selected, id]);
      }
      emit();
    },

    clear() {
      if (selected.size === 0) return;
      selected = new Set();
      emit();
    },

    /** Replace the selection with every provided id of the current element type. */
    selectAll(ids) {
      if (meshId == null) return;
      const next = new Set(Array.isArray(ids) ? ids.filter(Boolean) : []);
      if (next.size === selected.size && [...next].every((id) => selected.has(id))) return;
      selected = next;
      emit();
    },
  };
}
